import React, { useState, useEffect, useMemo, useRef } from "react";
import API from "../services/api";
import { useCanEdit, ReadOnlyBanner, READ_ONLY_HINT } from "../services/permissions";
import { format } from "date-fns";
import { Search, Download, RefreshCw, FileText, Eye, CheckCircle, Circle, ArrowUpRight, TrendingUp, Upload, ChevronLeft, ChevronRight, Trash2, DollarSign, Check, X, Edit2, Paperclip, Package } from "lucide-react";
import ProductFinder, { Highlight, matchesAny } from "./ProductFinder";

// Today's date in NZ (Pacific/Auckland) as YYYY-MM-DD, independent of the
// browser/server timezone. Using toISOString() here would give the UTC date,
// which is a day behind for most of the NZ working day.
const nzToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });

// Count-up number for the metric cards — animates from 0 to the value once
// on mount / value change, in ~600ms, and jumps straight to the value when
// the user prefers reduced motion.
function CountUp({ value }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const target = Number(value) || 0;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || target === 0) { setDisplay(target); return; }
    const start = performance.now();
    const from = 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / 600);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(Math.round(from + (target - from) * eased));
      if (t < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  return <>{display}</>;
}

// Extraction-quality chip shown in the Parse column so operators can spot
// orders that need attention at a glance.
// Machine reason codes -> what the operator actually has to DO about it.
export const REASON_LABELS = {
  no_products: "No items",
  empty_sales_order: "Blank sales order - ask store",
  po_summary_only: "PO summary only - ask store for the PO lines",
  destination_missing: "No destination",
  origin_missing: "No pickup",
  no_document_number: "No invoice/PO",
  unclassified_document: "Unknown doc type",
  generic_customer_name: "No customer name",
};
const reasonText = (r) => REASON_LABELS[r] || String(r).replace(/_/g, " ");

const renderParseChip = (status, reasons, warnings) => {
  const map = {
    ok: { label: "OK", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
    low_confidence: { label: "Needs info", cls: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
    unidentified: { label: "Unidentified", cls: "text-red-400 border-red-500/30 bg-red-500/10" },
  };
  // An absent/unknown parse_status is NOT a parse failure - default to a
  // neutral chip instead of the red "Unidentified" alarm.
  const s = map[status] || { label: "—", cls: "text-slate-500 border-slate-600/40 bg-slate-700/20" };
  const list = Array.isArray(reasons) ? reasons : [];
  // Say WHAT is missing, not just that something is. "Low conf" gave the
  // operator nothing to act on; "Needs info · No destination" does.
  let detail = list.map(reasonText).join(" · ");
  // The operator's rule (8 Oct 2026): a BT whose route and invoice are
  // known but whose item list is missing is OK - green - and says so.
  const warns = Array.isArray(warnings) ? warnings : [];
  const noItemList = status === "ok" && warns.some((w) => String(w).startsWith("No item list"));
  if (noItemList) detail = "No item list";
  return (
    <span className="inline-flex flex-col items-center gap-0.5" title={noItemList ? "No item list - collect everything on the invoice/PO named" : (detail || undefined)}>
      <span className={`inline-block px-1.5 py-0.5 rounded-full border text-[8px] font-semibold uppercase tracking-wide whitespace-nowrap ${s.cls}`}>
        {s.label}
      </span>
      {(status !== "ok" || noItemList) && detail && (
        <span className="text-[8px] leading-tight text-slate-500 max-w-[92px] truncate">{detail}</span>
      )}
    </span>
  );
};

export default function Dashboard({ onSelectOrder }) {
  const canEdit = useCanEdit();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOrderType, setFilterOrderType] = useState("all");
  const [filterStore, setFilterStore] = useState("");
  const [filterParse, setFilterParse] = useState("");
  const [filterReason, setFilterReason] = useState("");   // what is MISSING
  // Default the dashboard to TODAY's orders (NZ date).
  const [filterDateFrom, setFilterDateFrom] = useState(nzToday());
  const [filterDateTo, setFilterDateTo] = useState(nzToday());
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [metrics, setMetrics] = useState({ total: 0, pickedUp: 0, delivered: 0, pending: 0 });
  const [parseMetrics, setParseMetrics] = useState(null);
  const [editingRateId, setEditingRateId] = useState(null);
  const [editingRateValue, setEditingRateValue] = useState("");
  const rateInputRef = useRef(null);
  // "Find a product" panel: a product search across every date and
  // tomorrow's Harvey Norman plan, independent of the board's filters.
  const [showFinder, setShowFinder] = useState(false);

  // Reload when filters change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadOrders(1);
      loadMetrics();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, filterStatus, filterStore, filterOrderType, filterParse, filterReason, filterDateFrom, filterDateTo]);

  const loadOrders = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStatus) params.append("status", filterStatus);
      // no bt_type param is sent from UI (BT filtering removed)
      if (filterOrderType && filterOrderType !== "all") params.append("bt_order_type", filterOrderType);
      if (filterStore) params.append("store", filterStore);
      if (filterParse) params.append("parse_status", filterParse);
      if (filterReason) params.append("parse_reason", filterReason);
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);
      params.append("page", page);
      params.append("limit", 50);

      const res = await API.get(`/jobs?${params.toString()}`);
      setOrders(res.data.orders || []);
      setPagination(res.data.pagination || { total: 0, page: 1, limit: 50, totalPages: 0 });
      // The KPI cards must agree with the table beneath them. This response
      // already carries metrics computed under the SAME filters; the old
      // /analytics call silently dropped Parse/Store/Missing params, so the
      // cards contradicted the list on every filtered view.
      if (res.data.metrics) {
        setMetrics({
          total: res.data.metrics.total || 0,
          pickedUp: res.data.metrics.pickedUp || 0,
          delivered: res.data.metrics.delivered || 0,
          pending: res.data.metrics.pending || 0,
        });
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadMetrics = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStatus) params.append("status", filterStatus);
      if (filterStore) params.append("store", filterStore);
      if (filterParse) params.append("parse_status", filterParse);
      if (filterReason) params.append("parse_reason", filterReason);
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);
      
      // cards are set from the /jobs response (same filters as the table);
      // /analytics/dashboard dropped the Parse/Store/Missing params entirely
    } catch (e) {
      console.error(e);
    }
    try {
      const pm = await API.get(`/jobs/parse-metrics`);
      setParseMetrics(pm.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await API.post("/scan");
      loadOrders(1);
      loadMetrics();
    } catch (e) {
      alert("Scan failed: " + e.message);
    }
    setScanning(false);
  };

  const handleForceScan = async () => {
    setScanning(true);
    try {
      const res = await API.post("/scan?force=true");
      if (res.data.status === "in_progress") {
        alert("A scan is already running. Please wait.");
        setScanning(false);
        return;
      }
      // Poll for completion every 5 seconds
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await API.get("/scan-status");
          if (!statusRes.data.running) {
            clearInterval(pollInterval);
            setScanning(false);
            loadOrders(1);
            loadMetrics();
            if (statusRes.data.error) {
              alert("Force scan error: " + statusRes.data.error);
            } else {
              const newOrders = statusRes.data.result?.new_orders || 0;
              alert(`Force scan complete! ${newOrders} new orders found.`);
            }
          }
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 5000);
    } catch (e) {
      alert("Force scan failed: " + e.message);
      setScanning(false);
    }
  };


  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);

    try {
      await API.post("/parser/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      loadOrders(1);
      loadMetrics();
      alert("Document parsed and order created successfully!");
    } catch (err) {
      alert("Upload and Parsing failed: " + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = null; // reset input
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStatus) params.append("status", filterStatus);
      if (filterStore) params.append("store", filterStore);
      if (filterParse) params.append("parse_status", filterParse);
      if (filterReason) params.append("parse_reason", filterReason);
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);
      
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const baseUrl = isLocal ? 'http://localhost:8080/v1' : '/api/v1';
      const res = await fetch(`${baseUrl}/analytics/export/pdf?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to export PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStatus) params.append("status", filterStatus);
      if (filterStore) params.append("store", filterStore);
      if (filterParse) params.append("parse_status", filterParse);
      if (filterReason) params.append("parse_reason", filterReason);
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);
      
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const baseUrl = isLocal ? 'http://localhost:8080/v1' : '/api/v1';
      const res = await fetch(`${baseUrl}/jobs/export/csv?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to export Excel/CSV");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleStatusToggle = async (order, field) => {
    try {
      // PATCH /status is the endpoint that actually persists the
      // picked_up/delivered/billed flags (and stamps the timestamps);
      // PUT /jobs silently ignores them
      await API.patch(`/jobs/${encodeURIComponent(order.id)}/status`, { [field]: !order[field] });
      await loadOrders(pagination.page);
      loadMetrics();
    } catch (e) {
      alert("Update failed: " + e.message);
    }
  };

  const handleDeleteOrder = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this order?")) return;
    try {
      await API.delete(`/jobs/${id}`);
      await loadOrders(pagination.page);
      loadMetrics();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleRateEdit = (e, order) => {
    e.stopPropagation();
    setEditingRateId(order.id);
    setEditingRateValue(order.rate != null ? order.rate.toFixed(2) : "");
    setTimeout(() => rateInputRef.current?.focus(), 50);
  };

  const handleRateSave = async (e, orderId) => {
    if (e) e.stopPropagation();
    try {
      await API.patch(`/jobs/${encodeURIComponent(editingRateId)}/status`, { rate: parseFloat(editingRateValue) });
      setEditingRateId(null);
      await loadOrders(pagination.page);
    } catch (err) {
      alert("Failed to update rate: " + err.message);
    }
  };

  const handleRateCancel = (e) => {
    if (e) e.stopPropagation();
    setEditingRateId(null);
    setEditingRateValue("");
  };

  const handleRateKeyDown = (e, orderId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRateSave(e, orderId);
    } else if (e.key === "Escape") {
      handleRateCancel(e);
    }
  };

  const formatProducts = (items) => {
    if (!items || !items.length) return "Not identified";
    return items.map((i) => {
      const name = i.product?.code || i.sku || i.product?.description || i.raw_line || "Item";
      return `${name} x${i.quantity || 1}`;
    }).join(", ");
  };

  // Structured products cell: first two items stacked, "+N more" for the
  // rest; movement docs legitimately have no product lines, so show a quiet
  // "n/a" for them instead of an alarm. The words of the current search are
  // marked so the operator sees WHY a row matched when it was the product.
  const itemText = (i) => [i.product?.code, i.sku, i.product?.description, i.raw_line].filter(Boolean).join(" ");
  const renderProducts = (order) => {
    let items = order.products || [];
    if (!items.length) {
      if (order.document_type === "goods_movement_control" && order.parse_status === "ok") {
        return <span className="text-slate-600">n/a — movement doc</span>;
      }
      return <span className="text-slate-600">—</span>;
    }
    const searching = !!search.trim();
    // A matching item hidden behind "+N more" would explain nothing, so
    // matching items float to the top of the cell while a search is on.
    if (searching && items.length > 2) {
      items = [...items].sort((a, b) => (matchesAny(itemText(b), search) ? 1 : 0) - (matchesAny(itemText(a), search) ? 1 : 0));
    }
    const line = (i, idx) => {
      const name = i.product?.code || i.sku || i.product?.description || i.raw_line || "Item";
      const descFull = i.product?.description && i.product?.code ? String(i.product.description) : null;
      // the 30-char cut would hide a match further along the description
      const desc = descFull ? (searching && matchesAny(descFull, search) ? descFull : descFull.slice(0, 30)) : null;
      return (
        <div key={idx} className="truncate leading-snug">
          <span className="text-slate-300"><Highlight text={name} query={search} /></span>
          {desc && <span className="text-slate-500"> · <Highlight text={desc} query={search} /></span>}
          <span className="text-slate-500 tnum"> ×{i.quantity || 1}</span>
        </div>
      );
    };
    return (
      <div title={formatProducts(items)}>
        {items.slice(0, 2).map(line)}
        {items.length > 2 && (
          <span className="inline-block mt-0.5 px-1.5 rounded bg-slate-800 text-slate-400 text-[10px] font-semibold">
            +{items.length - 2} more
          </span>
        )}
      </div>
    );
  };

  // Single-day label for the "Searching <date> only" hint under the search bar.
  const dayLabel = (d) => {
    try {
      return format(new Date(String(d).slice(0, 10) + "T00:00:00"), "EEE dd MMM yyyy");
    } catch {
      return d;
    }
  };

  const formatDate = (d) => {
    if (!d) return "Not identified";
    try {
      return format(new Date(d), "dd/MM/yyyy HH:mm");
    } catch {
      return d;
    }
  };

  // Picked up / delivered / billed each PATCH the job the moment they are
  // clicked. For a reader the tick still says exactly what it said before,
  // it just stops being a control - no pointer affordance, and no tap that
  // would come back 403.
  const renderStatusCell = (order, field) => (
    <td
      className="px-4 py-3.5 text-center"
      title={canEdit ? undefined : READ_ONLY_HINT}
      onClick={canEdit ? (e) => { e.stopPropagation(); handleStatusToggle(order, field); } : undefined}
    >
      {order[field] ? (
        <CheckCircle size={17} className="text-green-500 mx-auto drop-shadow-[0_0_4px_rgba(34,197,94,0.2)]" />
      ) : (
        <Circle size={17} className={`text-slate-700 mx-auto ${canEdit ? "hover:text-slate-500" : ""}`} />
      )}
    </td>
  );


  return (
    <div className="space-y-6">
      {!canEdit && <ReadOnlyBanner />}

      {/* Find a product. Backdrop click, the X and Escape (handled inside
          the panel) all close it; picking an order closes it first so the
          detail view is not left under the overlay. */}
      {showFinder && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-[6vh]"
          onClick={() => setShowFinder(false)}
        >
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <ProductFinder
              onClose={() => setShowFinder(false)}
              onSelectOrder={(o) => { setShowFinder(false); onSelectOrder(o); }}
            />
          </div>
        </div>
      )}

      {/* Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {[
          { title: "Total Orders", val: metrics.total, desc: "Scanned & logged", accent: "bg-blue-500" },
          { title: "Picked Up", val: metrics.pickedUp, desc: "En route / loaded", accent: "bg-amber-500" },
          { title: "Delivered", val: metrics.delivered, desc: "Confirmed arrival", accent: "bg-emerald-500" },
          { title: "Pending", val: metrics.pending, desc: "Awaiting execution", accent: "bg-violet-500" },
        ].map((c, i) => (
          <div key={i} style={{ animationDelay: `${i * 60}ms` }}
            className="metric-card group relative bg-slate-950/50 border border-slate-800/90 rounded-xl px-3.5 py-2.5 overflow-hidden transition-all duration-200 hover:border-slate-700 hover:-translate-y-0.5">
            <div className={`absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r ${c.accent} opacity-70 transition-all group-hover:opacity-100 group-hover:top-1.5 group-hover:bottom-1.5`}></div>
            <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-[0.12em] pl-2.5">{c.title}</p>
            <div className="flex items-baseline gap-2 pl-2.5">
              <span className="tnum text-[22px] leading-tight font-bold text-white tracking-tight"><CountUp value={c.val} /></span>
              <span className="text-[10px] text-slate-500">{c.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Extraction quality strip - click a status to filter the table */}
      {parseMetrics && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 bg-slate-950/50 px-4 py-3 rounded-2xl border border-slate-800/90 text-xs">
          <span className="uppercase tracking-[0.14em] text-[10px] font-semibold text-slate-500">Extraction quality</span>
          <div className="flex items-center gap-1.5 bg-slate-900/80 border border-slate-800 rounded-full p-1">
            {[
              { key: "ok", label: "OK", count: parseMetrics.status_counts?.ok ?? 0, dot: "bg-emerald-400", on: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
              { key: "low_confidence", label: "Low conf", count: parseMetrics.status_counts?.low_confidence ?? 0, dot: "bg-amber-400", on: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
              { key: "unidentified", label: "Unidentified", count: parseMetrics.unidentified ?? 0, dot: "bg-rose-400", on: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
            ].map(s => (
              <button key={s.key} onClick={() => setFilterParse(filterParse === s.key ? "" : s.key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-all ${
                  filterParse === s.key ? s.on : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
                {s.label} <span className="tnum">{s.count}</span>
              </button>
            ))}
          </div>
          {(parseMetrics.top_failing_formats || []).length > 0 && (
            <span className="text-slate-500">
              Top failing: <span className="text-slate-400">{parseMetrics.top_failing_formats.slice(0, 3).map(f => `${f.format} (${f.count})`).join(", ")}</span>
            </span>
          )}
          {(parseMetrics.pending_by_store || []).length > 0 && (
            <span className="text-slate-500">
              Pickups: <span className="text-blue-400/90">{parseMetrics.pending_by_store.slice(0, 4).map(s => `${s.store} (${s.count})`).join(", ")}</span>
            </span>
          )}
        </div>
      )}

      {/* Action Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-950/30 p-4 rounded-2xl border border-slate-800/80">
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[300px]">
          {/* Search Bar. The board defaults to today, so a search that finds
              nothing is usually the date, not the text - say so under the bar
              and offer the one-click way out. */}
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <div className="flex items-center bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 focus-within:border-blue-500 transition-all">
              <Search size={16} className="text-slate-400 mr-2" />
              <input
                type="text"
                placeholder="Search order, invoice, client, product or store…"
                className="outline-none bg-transparent text-sm w-full text-slate-200 placeholder:text-slate-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {search.trim() && filterDateFrom && filterDateFrom === filterDateTo && (
              <p className="text-[11px] text-slate-500 px-1">
                Searching {dayLabel(filterDateFrom)} only ·{" "}
                <button
                  type="button"
                  onClick={() => {
                    // an explicit window: with both dates blank the server
                    // bounds the board to its 15-day default, which is not
                    // what "all dates" promises
                    const from = new Date();
                    from.setDate(from.getDate() - 400);
                    setFilterDateFrom(from.toISOString().slice(0, 10));
                    setFilterDateTo(nzToday());
                  }}
                  className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
                >
                  Search the last 400 days
                </button>
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowFinder(true)}
            title="Search every product line across orders and tomorrow's Harvey Norman plan"
            className="bg-slate-900 hover:bg-slate-800 text-amber-300 border border-amber-500/25 px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap transition-all"
          >
            <Package size={14} /> Find a product
          </button>

          {/* Filters */}
          <select 
            className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-slate-300 outline-none cursor-pointer focus:border-blue-500" 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>

          {/* Parse / extraction-quality filter */}
          <select
            className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-slate-300 outline-none cursor-pointer focus:border-blue-500"
            value={filterParse}
            onChange={(e) => setFilterParse(e.target.value)}
          >
            <option value="">Parse: All</option>
            <option value="ok">OK</option>
            <option value="low_confidence">Needs info</option>
            <option value="unidentified">Unidentified</option>
          </select>

          {/* What's missing - lets the team clear one problem across the whole
              board in a batch instead of opening every amber row to find out
              what it needs. */}
          <select
            className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-slate-300 outline-none cursor-pointer focus:border-blue-500"
            value={filterReason}
            onChange={(e) => setFilterReason(e.target.value)}
          >
            <option value="">Missing: Any</option>
            {Object.entries(REASON_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>

          {/* Branch transfer filter removed per user request */}

          <select 
            className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-slate-300 outline-none cursor-pointer focus:border-blue-500" 
            value={filterOrderType} 
            onChange={(e) => setFilterOrderType(e.target.value)}
          >
            <option value="all">All Route Types</option>
            <option value="Local">Local</option>
            <option value="Line-Haul">Line-Haul</option>
            <option value="Line-Haul + Local">Line-Haul + Local</option>
            <option value="Line-Haul - South">Line-Haul - South</option>
            <option value="Line-Haul - South + Local">Line-Haul - South + Local</option>
            <option value="Line-Haul + Line-Haul South">Line-Haul + Line-Haul South</option>
            <option value="Not identified">Not identified</option>
          </select>

          <input 
            type="text" 
            placeholder="Filter by Store..."
            className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-slate-300 outline-none w-36 focus:border-blue-500"
            value={filterStore}
            onChange={(e) => setFilterStore(e.target.value)}
          />

          <div className="flex items-center gap-2 bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2">
            <span className="text-slate-400 text-xs">Date:</span>
            <input
              type="date"
              className="bg-transparent text-xs text-slate-300 outline-none w-[110px] [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              placeholder="From"
            />
            <span className="text-slate-600">-</span>
            <input
              type="date"
              className="bg-transparent text-xs text-slate-300 outline-none w-[110px] [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              placeholder="To"
            />
          </div>
        </div>

        {/* Buttons. Scanning and uploading both create orders on the board,
            so they are office work; the two exports below are reading a
            report and stay available to everyone. */}
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={handleScan}
              disabled={scanning}
              className="bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-50 transition-all shadow-lg shadow-blue-600/10"
            >
              <RefreshCw size={13} className={scanning ? "animate-spin" : ""} />
              {scanning ? "Scanning..." : "Scan Emails"}
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleForceScan}
              disabled={scanning}
              className="bg-orange-600 text-white px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 hover:bg-orange-700 disabled:opacity-50 transition-all shadow-lg shadow-orange-600/10"
            >
              <RefreshCw size={13} className={scanning ? "animate-spin" : ""} />
              Force Rescan (Last 50)
            </button>
          )}
          {canEdit && (
            <label className="bg-slate-900 hover:bg-slate-800 text-blue-400 border border-blue-500/25 px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 cursor-pointer transition-all shadow-md">
              <Upload size={13} className={uploading ? "animate-bounce" : ""} />
              {uploading ? "Parsing AI..." : "Upload Document"}
              <input type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.png,.jpeg,.jpg,.docx" />
            </label>
          )}
          <button
            onClick={handleExportPDF} 
            className="bg-slate-900 hover:bg-slate-800 text-red-400 border border-red-500/25 px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all"
          >
            <FileText size={13} /> PDF Report
          </button>
          <button 
            onClick={handleExportExcel} 
            className="bg-slate-900 hover:bg-slate-800 text-green-400 border border-green-500/25 px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all"
          >
            <Download size={13} /> Excel
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-slate-950/50 rounded-2xl shadow-2xl shadow-black/20 border border-slate-800/90 overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm text-slate-300 table-auto">
            <thead className="sticky top-0 z-10 bg-[#0d1526]/95 backdrop-blur text-slate-500 border-b border-slate-800 text-[10px] uppercase tracking-[0.12em] font-semibold">
              <tr>
                <th className="px-4 py-3 text-left">Invoice #</th>
                <th className="px-4 py-3 text-left">Order #</th>
                <th className="px-4 py-3 text-left">Doc Type</th>
                <th className="px-4 py-3 text-center">Fmt</th>
                <th className="px-4 py-3 text-center">Parse</th>
                <th className="px-4 py-3 text-left">Subject</th>
                <th className="px-4 py-3 text-left">Email Date</th>
                <th className="px-4 py-3 text-left">Products</th>
                <th className="px-4 py-3 text-left">BT Route Type</th>
                <th className="px-4 py-3 text-left">Coming From</th>
                <th className="px-4 py-3 text-left">Destination</th>
                <th className="px-4 py-3 text-left">Bill To</th>
                <th className="px-4 py-3 text-center">Picked up</th>
                <th className="px-4 py-3 text-center">Delivered</th>
                <th className="px-4 py-3 text-center">Billed</th>
                <th className="px-4 py-3 text-left">Rate</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={18} className="px-4 py-3">
                      <div className="skeleton h-5 w-full" style={{ opacity: 1 - i * 0.13 }}></div>
                    </td>
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={18} className="px-4 py-16 text-center">
                    <Search size={26} className="mx-auto mb-3 text-slate-600" />
                    <p className="text-slate-400 text-sm font-medium">No orders match these filters</p>
                    <p className="text-slate-600 text-xs mt-1">Try widening the date range or clearing the Parse filter.</p>
                  </td>
                </tr>
              ) : (
                orders.map((order, ri) => {
                  const rowTint = order.parse_status === "ok" ? "row-ok"
                    : order.parse_status === "low_confidence" ? "row-low"
                    : order.parse_status === "unidentified" ? "row-unident" : "";
                  return (
                  <tr
                    key={order.id}
                    className={`${ri % 2 ? "bg-slate-900/20" : ""} ${rowTint} hover:bg-blue-500/[0.06] cursor-pointer group`}
                    onClick={() => onSelectOrder(order)}
                  >
                    <td className="px-4 py-3 font-mono text-xs tnum">
                      <span className="flex items-center gap-1.5">
                        <span className={order.invoiceNo ? "text-slate-200" : "text-slate-600"}>
                          {order.invoiceNo || "—"}
                        </span>
                        {((order.attachments && order.attachments.length > 0) || (order.attachment_paths && order.attachment_paths.length > 0)) && (
                          <Paperclip size={11} className="text-amber-400/80 shrink-0" title="Has attachments" />
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs tnum text-indigo-400 font-semibold">
                      {order.order_number || order.po_number || "—"}
                    </td>
                    <td className="px-4 py-3.5 text-slate-300 text-xs font-semibold capitalize whitespace-nowrap">
                      {order.type ? order.type.replace(/_/g, ' ') : "Generic"}
                    </td>
                    <td className="px-4 py-3.5 text-center text-slate-400 text-xs">
                      {order.bt_format && order.bt_format !== "unknown" ? order.bt_format : "–"}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {renderParseChip(order.parse_status, order.parse_reasons, order.parse_warnings)}
                    </td>
                    <td className="px-4 py-3.5 max-w-[210px] truncate text-slate-300 text-xs" title={order.sourceEmailSubject}>
                      {order.sourceEmailSubject || <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-slate-400 text-xs">
                      {formatDate(order.email_date)}
                    </td>
                    <td className="px-4 py-3 max-w-[230px] min-w-[160px] text-xs align-top">
                      {renderProducts(order)}
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-xs truncate max-w-[120px]" title={order.bt_order_type}>
                      {order.bt_order_type || <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-300 text-xs truncate max-w-[100px]" title={order.comingFrom}>
                      {order.comingFrom || <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-300 text-xs truncate max-w-[100px]" title={order.destination}>
                      {order.destination || <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-300 text-xs truncate max-w-[120px]" title={order.billTo}>
                      {order.billTo || <span className="text-slate-600">—</span>}
                    </td>
                    
                    {/* Status Toggles */}
                    {renderStatusCell(order, "picked_up")}
                    {renderStatusCell(order, "delivered")}
                    {renderStatusCell(order, "billed")}

                    <td className="px-4 py-3.5 font-mono text-xs" onClick={(e) => e.stopPropagation()}>
                      {!canEdit ? (
                        /* Plain text, not a dead-looking button: the rate is
                           what gets invoiced, and only admin/IT may set it. */
                        <span className="font-semibold text-emerald-400" title={READ_ONLY_HINT}>
                          {order.rate != null ? `$${order.rate.toFixed(2)}` : "—"}
                        </span>
                      ) : editingRateId === order.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-emerald-400">$</span>
                          <input
                            ref={rateInputRef}
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-20 bg-slate-800 border border-emerald-500/50 rounded-lg px-2 py-1 text-emerald-300 text-xs font-mono outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all"
                            value={editingRateValue}
                            onChange={(e) => setEditingRateValue(e.target.value)}
                            onKeyDown={(e) => handleRateKeyDown(e, order.id)}
                            onBlur={() => setTimeout(() => { if (editingRateId === order.id) handleRateCancel(); }, 200)}
                            placeholder="0.00"
                          />
                          <button
                            onClick={(e) => handleRateSave(e, order.id)}
                            className="text-emerald-400 hover:text-emerald-300 p-0.5 rounded transition-colors"
                            title="Save (Enter)"
                          >
                            <Check size={13} />
                          </button>
                          <button
                            onClick={handleRateCancel}
                            className="text-slate-500 hover:text-red-400 p-0.5 rounded transition-colors"
                            title="Cancel (Esc)"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => handleRateEdit(e, order)}
                          className="group/rate flex items-center gap-1 font-semibold text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer"
                          title="Click to edit rate"
                        >
                          {order.rate != null ? `$${order.rate.toFixed(2)}` : "—"}
                          <Edit2 size={12} className="text-slate-600 group-hover/rate:text-emerald-400/60 transition-colors" />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3.5 max-w-[140px] truncate text-slate-400 text-xs" title={order.destination}>
                      {order.destination || "Not identified"}
                    </td>
                    <td className="px-4 py-3.5 text-center flex items-center justify-center gap-1.5">
                      <button 
                        onClick={(e) => { e.stopPropagation(); onSelectOrder(order); }}
                        className="bg-slate-900 border border-slate-800 group-hover:border-blue-500/30 text-slate-400 hover:text-blue-400 p-1.5 rounded-lg transition-all"
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      {canEdit && (
                        <button
                          onClick={(e) => handleDeleteOrder(e, order.id)}
                          className="bg-slate-900 border border-slate-800 group-hover:border-rose-500/30 text-slate-400 hover:text-rose-400 p-1.5 rounded-lg transition-all"
                          title="Delete Order"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-slate-950/30 rounded-2xl border border-slate-800/80">
          <span className="text-xs text-slate-400">
            Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} orders
          </span>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => loadOrders(pagination.page - 1)} 
              disabled={pagination.page <= 1}
              className="bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-slate-900 rounded-xl px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition-all shadow-md"
            >
              <ChevronLeft size={13} /> Prev
            </button>
            <span className="bg-slate-950 border border-slate-800/80 text-blue-400 font-semibold px-4 py-2 rounded-xl text-xs">
              Page {pagination.page} / {pagination.totalPages}
            </span>
            <button 
              onClick={() => loadOrders(pagination.page + 1)} 
              disabled={pagination.page >= pagination.totalPages}
              className="bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-slate-900 rounded-xl px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition-all shadow-md"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
